import React, { useState, useEffect } from 'react';
import {
  ArrowDownTrayIcon,
  CloudArrowDownIcon,
  FolderIcon,
  CloudIcon,
  BuildingOffice2Icon,
  CircleStackIcon,
  DocumentArrowDownIcon,
  InformationCircleIcon,
  TagIcon,
  PresentationChartBarIcon
} from '@heroicons/react/24/outline';
import AttributeLabel from '../components/AttributeLabel';
import ConfirmationModal from '../components/ConfirmationModal';
import DownloadProgressModal from '../components/DownloadProgressModal';
import DatabaseBackup from '../components/DatabaseBackup';
import CategoryManagementModal from '../components/modals/CategoryManagementModal';
import { useDocumentsDownloadProgress, useDogsDownloadProgress } from '../hooks/useDownloadProgress';
import apiClient from '../utils/apiClient';

interface ConfirmModalState {
  visible: boolean;
  type: 'success' | 'warning' | 'error' | 'question' | 'info';
  title: string;
  message: string;
  variant: 'single' | 'two';
  onClose?: () => void;
  buttons?: {
    cancel: { text: string; onPress: () => void };
    confirm: { text: string; onPress: () => void };
  };
  button?: { text: string; onPress: () => void };
}

/**
 * Fehlermeldung aus einer fehlgeschlagenen Blob-Antwort lesen.
 *
 * Bei `responseType: 'blob'` liefert axios auch im Fehlerfall einen Blob, nicht
 * das geparste JSON. `err.response.data.message` ist deshalb immer undefined –
 * bislang sah der Nutzer ausschließlich den generischen Text, während die
 * eigentliche Ursache (fehlende Tabelle, Datenbankfehler) nur im Serverlog
 * stand. Der Blob enthält die JSON-Antwort und muss ausgelesen werden.
 */
const fehlerText = async (err: any, fallback: string): Promise<string> => {
  const status = err?.response?.status;

  // 404 heißt hier nicht „nichts gefunden", sondern „diese Route kennt der
  // Server nicht". Das passiert genau dann, wenn das Backend nach einer
  // Aktualisierung nicht neu gestartet wurde — der generische Text hat das
  // bislang verschleiert.
  if (status === 404) {
    return `${fallback} Der Server kennt diese Funktion nicht (HTTP 404). `
      + 'Vermutlich läuft das Backend noch mit einem älteren Stand und muss neu gestartet werden.';
  }

  const daten = err?.response?.data;
  let meldung: string | null = null;

  if (daten instanceof Blob) {
    try {
      meldung = JSON.parse(await daten.text())?.message || null;
    } catch {
      meldung = null;
    }
  } else {
    meldung = daten?.message || null;
  }

  if (meldung) return meldung;
  return status ? `${fallback} (HTTP ${status})` : fallback;
};

/**
 * Tagesordnungspunkte der Versammlungs-Präsentation.
 *
 * Reihenfolge und Schlüssel entsprechen `KAPITEL` in
 * `Backend/src/utils/versammlungPraesentation.js`; der Server numeriert die
 * Tagesordnung genau aus den hier angehakten Punkten.
 */
const VERSAMMLUNG_KAPITEL: { key: string; label: string; hinweis?: string }[] = [
  { key: 'tagesordnung', label: 'Tagesordnung', hinweis: 'Übersichtsfolie aus den gewählten Punkten' },
  { key: 'mitglieder', label: 'Mitgliederentwicklung', hinweis: 'Bestand, Zu- und Abgänge, fünf Jahre' },
  { key: 'taetigkeitsbericht', label: 'Bericht des Vorstands', hinweis: 'Training und Höhepunkte des Jahres' },
  { key: 'veranstaltungen', label: 'Veranstaltungen und Sport', hinweis: 'Termine, Anmeldungen, Prüfungen' },
  { key: 'kassenbericht', label: 'Kassenbericht', hinweis: 'Einnahmen und Ausgaben mit Vorjahresvergleich' },
  { key: 'vermoegen', label: 'Vermögensübersicht', hinweis: 'Kassenstände und Anlagevermögen zum 31.12.' },
  { key: 'ruecklagen', label: 'Rücklagen', hinweis: 'Zweckbindung und Stand' },
  { key: 'kassenpruefung', label: 'Bericht der Kassenprüfer', hinweis: 'Prüfer und hinterlegter Bericht' },
  { key: 'beitraege', label: 'Beiträge', hinweis: 'Beitragssätze und offene Beitragsjahre (als Summe)' },
  { key: 'entlastung', label: 'Entlastung des Vorstands', hinweis: 'Abstimmungsfolie' },
  { key: 'wahlen', label: 'Wahlen', hinweis: 'Leere Abstimmungsfolie' },
  { key: 'ausblick', label: 'Ausblick und Haushalt', hinweis: 'Haushaltsplan des Folgejahres, falls angelegt' },
  { key: 'antraege', label: 'Anträge', hinweis: 'Abstimmungsfolie' },
];

const AdminFinanceExport: React.FC = () => {
  // Download progress hooks
  const documentsDownload = useDocumentsDownloadProgress();
  const dogsDownload = useDogsDownloadProgress();

  // Category Modal
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  // Confirmation Modal
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>({
    visible: false,
    type: 'info',
    title: '',
    message: '',
    variant: 'single'
  });

  // Jahresordner Export States
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [selectedExportYear, setSelectedExportYear] = useState<number>(new Date().getFullYear());
  const [includeDocuments, setIncludeDocuments] = useState<boolean>(true);
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [finanzamtExportLoading, setFinanzamtExportLoading] = useState<boolean>(false);
  const [bankStatementsExportLoading, setBankStatementsExportLoading] = useState<boolean>(false);

  // Kapitelauswahl für Jahresordner
  const [selectedChapters, setSelectedChapters] = useState({
    mitgliederliste: true,
    hundeuebersicht: true,
    finanzuebersicht: true,
    finanzamt: true,
    dokumente: true
  });

  // Kapitelauswahl für Finanzamt-Export
  const [selectedFinanzamtChapters, setSelectedFinanzamtChapters] = useState({
    einnahmenAusgaben: true,
    vermoegensaufstellung: true,
    taetigkeitsbericht: true,
    veranstaltungsstatistik: true,
    ruecklagennachweis: true
  });
  const [showFinanzamtHelp, setShowFinanzamtHelp] = useState(false);

  // Präsentation zur Mitgliederversammlung (16:9)
  //
  // Eigenes Jahr, unabhängig vom Jahresordner: die Versammlung berichtet über
  // das abgeschlossene Geschäftsjahr, während oben meist das laufende gewählt ist.
  const [selectedVersammlungYear, setSelectedVersammlungYear] = useState<number>(new Date().getFullYear() - 1);
  const [versammlungLoading, setVersammlungLoading] = useState<boolean>(false);
  const [selectedVersammlungChapters, setSelectedVersammlungChapters] = useState<Record<string, boolean>>({
    tagesordnung: true,
    mitglieder: true,
    taetigkeitsbericht: true,
    veranstaltungen: true,
    kassenbericht: true,
    vermoegen: true,
    ruecklagen: true,
    kassenpruefung: true,
    beitraege: true,
    entlastung: true,
    wahlen: false,
    ausblick: true,
    antraege: true,
  });

  useEffect(() => {
    fetchAvailableYears();
  }, []);

  const fetchAvailableYears = async () => {
    try {
      const response = await apiClient.get('/api/backup/yearly-report/years');
      if (response.data.success && response.data.data) {
        const jahre: number[] = response.data.data;
        setAvailableYears(jahre);
        if (jahre.length > 0) {
          setSelectedExportYear(jahre[0]);
          // Für die Versammlung das jüngste abgeschlossene Jahr vorschlagen —
          // über ein noch laufendes Geschäftsjahr wird nicht berichtet.
          const laufend = new Date().getFullYear();
          setSelectedVersammlungYear(jahre.find((j) => j < laufend) ?? jahre[0]);
        }
      }
    } catch (err) {
      console.error('Error fetching available years:', err);
      const currentYear = new Date().getFullYear();
      setAvailableYears([currentYear, currentYear - 1, currentYear - 2, currentYear - 3, currentYear - 4]);
    }
  };

  const handleDownloadAll = () => {
    documentsDownload.startDownload();
  };

  const handleDownloadAllMembersDogs = () => {
    dogsDownload.startDownload();
  };

  const handleBankStatementsExport = async () => {
    setBankStatementsExportLoading(true);
    try {
      const response = await apiClient.get('/api/finance/bank-statements/export', {
        responseType: 'blob',
        timeout: 180000
      });

      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Kontoauszuege_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setConfirmModal({
        visible: true,
        type: 'success',
        title: 'Kontoauszüge gesichert',
        message: 'Die Kontoauszüge wurden nach Konto und Jahr sortiert als ZIP-Datei heruntergeladen.',
        variant: 'single',
        button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
      });
    } catch (err: any) {
      console.error('Error exporting bank statements:', err);
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Export fehlgeschlagen',
        message: await fehlerText(err, 'Fehler beim Sichern der Kontoauszüge.'),
        variant: 'single',
        button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
      });
    } finally {
      setBankStatementsExportLoading(false);
    }
  };

  const handleYearlyReportExport = async () => {
    setExportLoading(true);
    try {
      const chaptersParam = encodeURIComponent(JSON.stringify(selectedChapters));
      const response = await apiClient.get(
        `/api/backup/yearly-report?year=${selectedExportYear}&includeDocuments=${includeDocuments}&chapters=${chaptersParam}`,
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Jahresordner_${selectedExportYear}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setConfirmModal({
        visible: true,
        type: 'success',
        title: 'Export erfolgreich',
        message: `Der Jahresordner für ${selectedExportYear} wurde erfolgreich erstellt und heruntergeladen.`,
        variant: 'single',
        button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
      });
    } catch (err: any) {
      console.error('Error exporting yearly report:', err);
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Export fehlgeschlagen',
        message: await fehlerText(err, 'Fehler beim Erstellen des Jahresordners.'),
        variant: 'single',
        button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
      });
    } finally {
      setExportLoading(false);
    }
  };

  const handleFinanzamtExport = async () => {
    setFinanzamtExportLoading(true);
    try {
      const chaptersParam = encodeURIComponent(JSON.stringify(selectedFinanzamtChapters));
      const response = await apiClient.get(
        `/api/backup/finanzamt-report?year=${selectedExportYear}&chapters=${chaptersParam}`,
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Finanzamt_Unterlagen_${selectedExportYear}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setConfirmModal({
        visible: true,
        type: 'success',
        title: 'Export erfolgreich',
        message: `Die Finanzamt-Unterlagen für ${selectedExportYear} wurden erfolgreich erstellt.`,
        variant: 'single',
        button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
      });
    } catch (err: any) {
      console.error('Error exporting Finanzamt report:', err);
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Export fehlgeschlagen',
        message: await fehlerText(err, 'Fehler beim Erstellen der Finanzamt-Unterlagen.'),
        variant: 'single',
        button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
      });
    } finally {
      setFinanzamtExportLoading(false);
    }
  };

  const handleVersammlungExport = async () => {
    setVersammlungLoading(true);
    try {
      const chaptersParam = encodeURIComponent(JSON.stringify(selectedVersammlungChapters));
      const response = await apiClient.get(
        `/api/backup/versammlung-praesentation?year=${selectedVersammlungYear}&chapters=${chaptersParam}`,
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Mitgliederversammlung_${selectedVersammlungYear}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setConfirmModal({
        visible: true,
        type: 'success',
        title: 'Präsentation erstellt',
        message: `Die Folien zur Mitgliederversammlung über das Geschäftsjahr ${selectedVersammlungYear} wurden heruntergeladen.`,
        variant: 'single',
        button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
      });
    } catch (err: any) {
      console.error('Error exporting Versammlungs-Präsentation:', err);
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Erstellung fehlgeschlagen',
        message: await fehlerText(err, 'Fehler beim Erstellen der Präsentation.'),
        variant: 'single',
        button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
      });
    } finally {
      setVersammlungLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <header className="app-page-header hidden sm:block bg-white dark:bg-gray-800 border-b-[3px] border-lime-400 mb-6">
        <div className="p-4 md:py-6 md:px-8 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <ArrowDownTrayIcon className="h-7 w-7 text-primary-600 dark:text-primary-400" />
            <div>
              <h1 className="text-[28px] font-bold text-gray-900 dark:text-white">Backup & Export</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Datensicherung, Finanzkategorien und Jahresberichte</p>
            </div>
          </div>
        </div>
      </header>

      <div className="space-y-6">
        {/* Backup-Quellen Section */}
        <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-primary-600 dark:text-primary-400 flex items-center gap-2.5 mb-6">
            <CloudIcon className="h-5 w-5" /> Backup-Quellen
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 border-l-4 border-info-500 dark:border-info-400 pl-3 py-1">
            Erstellen Sie Backups Ihrer Dokumente.
          </p>

          <div className="md:col-span-2">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <CloudArrowDownIcon className="h-5 w-5 text-primary-600 dark:text-primary-500" />
              Datensicherung
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Erstellen Sie ein Backup aller Dokumente, der Hunde-Dokumente oder nur der Kontoauszüge. Verschlüsselte Dateien werden für den Download entschlüsselt und unverschlüsselt auf Ihrem Gerät gespeichert.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 mb-6">
              <button
                onClick={handleDownloadAll}
                disabled={documentsDownload.progress.active || dogsDownload.progress.active || bankStatementsExportLoading}
                className="px-4 py-2.5 bg-primary-600 dark:bg-primary-500 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                <span className="text-sm">Alle Dokumente sichern</span>
              </button>
              <button
                onClick={handleDownloadAllMembersDogs}
                disabled={documentsDownload.progress.active || dogsDownload.progress.active || bankStatementsExportLoading}
                className="px-4 py-2.5 bg-primary-600 dark:bg-primary-500 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                <span className="text-sm">Alle Hunde-Dokumente sichern</span>
              </button>
              <button
                onClick={handleBankStatementsExport}
                disabled={documentsDownload.progress.active || dogsDownload.progress.active || bankStatementsExportLoading}
                className="px-4 py-2.5 bg-primary-600 dark:bg-primary-500 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                <DocumentArrowDownIcon className={`h-4 w-4 ${bankStatementsExportLoading ? 'animate-pulse' : ''}`} />
                <span className="text-sm">{bankStatementsExportLoading ? 'Kontoauszüge werden gepackt …' : 'Kontoauszüge sichern'}</span>
              </button>
            </div>

            <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <CircleStackIcon className="h-5 w-5 text-primary-600 dark:text-primary-500" />
                Datenbank-Backup
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Exportieren Sie ausgewählte Anwendungsdaten als JSON oder erstellen Sie einen vollständigen SQL-Dump der gesamten Datenbank.
              </p>
              <DatabaseBackup />
            </div>
          </div>
        </section>

        {/* Finanzkategorien Section */}
        <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-primary-600 dark:text-primary-400 flex items-center gap-2.5 mb-6">
            <TagIcon className="h-5 w-5" /> Finanzkategorien
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Verwalten Sie die Kategorien für Buchungen und ordnen Sie ihnen steuerliche Sphären zu
            (ideell, Vermögen, Zweckbetrieb, wirtschaftlich) für die Steuererklärung.
          </p>
          <button
            onClick={() => setShowCategoryModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
          >
            <TagIcon className="h-5 w-5" />
            Kategorien verwalten
          </button>
        </section>

        {/* Jahresordner Export Section */}
        <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-primary-600 dark:text-primary-400 flex items-center gap-2.5 mb-6">
            <DocumentArrowDownIcon className="h-5 w-5" /> Jahresordner Export
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Erstellen Sie einen kompletten Jahresordner als PDF mit Deckblatt, Inhaltsverzeichnis,
            Mitgliederliste, Hundeübersicht, Kontobuchungen und allen Dokumenten des Jahres.
          </p>

          {/* Jahr Auswahl */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600 mb-6">
            <AttributeLabel iconKey="year" className="mb-2" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
              Jahr auswählen
            </AttributeLabel>
            <select
              value={selectedExportYear}
              onChange={(e) => setSelectedExportYear(Number(e.target.value))}
              className="w-full sm:w-48 px-3 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          {/* Zwei Spalten für Jahresordner und Finanzamt */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Linke Spalte: Kompletter Jahresordner */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-5 border border-gray-200 dark:border-gray-600 flex flex-col">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <FolderIcon className="h-5 w-5 text-primary-600" />
                Kompletter Jahresordner
              </h3>

              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Wählen Sie die Kapitel, die enthalten sein sollen:
              </p>

              <div className="space-y-3 mb-5">
                <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 p-2 rounded-lg transition-colors">
                  <input
                    type="checkbox"
                    checked={selectedChapters.mitgliederliste}
                    onChange={(e) => setSelectedChapters(prev => ({ ...prev, mitgliederliste: e.target.checked }))}
                    className="w-5 h-5 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                  />
                  <span className="text-gray-700 dark:text-gray-300">Mitgliederliste</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 p-2 rounded-lg transition-colors">
                  <input
                    type="checkbox"
                    checked={selectedChapters.hundeuebersicht}
                    onChange={(e) => setSelectedChapters(prev => ({ ...prev, hundeuebersicht: e.target.checked }))}
                    className="w-5 h-5 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                  />
                  <span className="text-gray-700 dark:text-gray-300">Hundeübersicht</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 p-2 rounded-lg transition-colors">
                  <input
                    type="checkbox"
                    checked={selectedChapters.finanzuebersicht}
                    onChange={(e) => setSelectedChapters(prev => ({ ...prev, finanzuebersicht: e.target.checked }))}
                    className="w-5 h-5 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                  />
                  <span className="text-gray-700 dark:text-gray-300">Finanzübersicht (Kategorien + Kontobuchungen)</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 p-2 rounded-lg transition-colors">
                  <input
                    type="checkbox"
                    checked={selectedChapters.finanzamt}
                    onChange={(e) => setSelectedChapters(prev => ({ ...prev, finanzamt: e.target.checked }))}
                    className="w-5 h-5 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                  />
                  <span className="text-gray-700 dark:text-gray-300">Unterlagen für das Finanzamt</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 p-2 rounded-lg transition-colors">
                  <input
                    type="checkbox"
                    checked={selectedChapters.dokumente}
                    onChange={(e) => {
                      setSelectedChapters(prev => ({ ...prev, dokumente: e.target.checked }));
                      setIncludeDocuments(e.target.checked);
                    }}
                    className="w-5 h-5 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                  />
                  <span className="text-gray-700 dark:text-gray-300">Dokumente anhängen (PDFs aus AWS S3)</span>
                </label>
              </div>

              <button
                onClick={handleYearlyReportExport}
                disabled={exportLoading || !Object.values(selectedChapters).some(v => v)}
                className="mt-auto w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white font-medium rounded-lg transition-colors"
              >
                {exportLoading ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Wird erstellt...
                  </>
                ) : (
                  <>
                    <ArrowDownTrayIcon className="h-5 w-5" />
                    Jahresordner {selectedExportYear} exportieren
                  </>
                )}
              </button>
            </div>

            {/* Rechte Spalte: Nur Finanzamt-Unterlagen */}
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-5 border border-amber-200 dark:border-amber-700 flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <BuildingOffice2Icon className="h-5 w-5 text-amber-600" />
                  Finanzamt-Unterlagen
                </h3>
                <button
                  onClick={() => setShowFinanzamtHelp(!showFinanzamtHelp)}
                  className="flex items-center gap-1 text-sm text-amber-700 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300 transition-colors"
                >
                  <InformationCircleIcon className="h-5 w-5" />
                  {showFinanzamtHelp ? 'Hilfe ausblenden' : 'Was wird benötigt?'}
                </button>
              </div>

              {/* Hilfe-Bereich */}
              {showFinanzamtHelp && (
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 mb-4 border border-amber-300 dark:border-amber-600">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2 text-sm">
                    Überprüfung der Geschäftsführung (§ 63 AO)
                  </h4>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                    Das Finanzamt für Körperschaften prüft regelmäßig die Gemeinnützigkeit. Folgende Unterlagen werden typischerweise angefordert:
                  </p>
                  <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-amber-600 font-bold">1.</span>
                      <span><strong>Einnahmen-Ausgaben-Rechnung</strong> – getrennt nach den 4 Sphären: Ideeller Bereich, Vermögensverwaltung, Zweckbetrieb, wirtschaftlicher Geschäftsbetrieb</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-600 font-bold">2.</span>
                      <span><strong>Tätigkeitsbericht</strong> – Protokolle der Mitgliederversammlungen, Veranstaltungsprogramme, Auskunft über Tätigkeiten</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-600 font-bold">3.</span>
                      <span><strong>Vermögensaufstellung</strong> – Konten, Kassen und Anlagevermögen mit Restbuchwerten zum 31.12.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-amber-600 font-bold">4.</span>
                      <span><strong>Rücklagennachweis</strong> – Bildung und Entwicklung der Rücklagen (§ 62 AO)</span>
                    </li>
                  </ul>
                </div>
              )}

              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Unterlagen für die Überprüfung der Gemeinnützigkeit:
              </p>

              <div className="space-y-2 mb-5">
                <label className="flex items-start gap-3 cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-800/30 p-2 rounded-lg transition-colors">
                  <input
                    type="checkbox"
                    checked={selectedFinanzamtChapters.einnahmenAusgaben}
                    onChange={(e) => setSelectedFinanzamtChapters(prev => ({ ...prev, einnahmenAusgaben: e.target.checked }))}
                    className="w-5 h-5 mt-0.5 text-amber-600 border-gray-300 dark:border-gray-600 rounded focus:ring-amber-500"
                  />
                  <div>
                    <span className="text-gray-700 dark:text-gray-300 font-medium">Einnahmen-Ausgaben-Rechnung</span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Getrennt nach 4 Sphären (ideell, Vermögen, Zweckbetrieb, wirtschaftlich)</p>
                  </div>
                </label>

                <label className="flex items-start gap-3 cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-800/30 p-2 rounded-lg transition-colors">
                  <input
                    type="checkbox"
                    checked={selectedFinanzamtChapters.vermoegensaufstellung}
                    onChange={(e) => setSelectedFinanzamtChapters(prev => ({ ...prev, vermoegensaufstellung: e.target.checked }))}
                    className="w-5 h-5 mt-0.5 text-amber-600 border-gray-300 dark:border-gray-600 rounded focus:ring-amber-500"
                  />
                  <div>
                    <span className="text-gray-700 dark:text-gray-300 font-medium">Vermögensaufstellung zum 31.12.</span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Konten, Kassen und Anlagevermögen mit Restbuchwerten</p>
                  </div>
                </label>

                <label className="flex items-start gap-3 cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-800/30 p-2 rounded-lg transition-colors">
                  <input
                    type="checkbox"
                    checked={selectedFinanzamtChapters.taetigkeitsbericht}
                    onChange={(e) => setSelectedFinanzamtChapters(prev => ({ ...prev, taetigkeitsbericht: e.target.checked }))}
                    className="w-5 h-5 mt-0.5 text-amber-600 border-gray-300 dark:border-gray-600 rounded focus:ring-amber-500"
                  />
                  <div>
                    <span className="text-gray-700 dark:text-gray-300 font-medium">Tätigkeitsbericht</span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Protokolle, Veranstaltungen, Aktivitäten im Prüfungszeitraum</p>
                  </div>
                </label>

                <label className="flex items-start gap-3 cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-800/30 p-2 rounded-lg transition-colors">
                  <input
                    type="checkbox"
                    checked={selectedFinanzamtChapters.veranstaltungsstatistik}
                    onChange={(e) => setSelectedFinanzamtChapters(prev => ({ ...prev, veranstaltungsstatistik: e.target.checked }))}
                    className="w-5 h-5 mt-0.5 text-amber-600 border-gray-300 dark:border-gray-600 rounded focus:ring-amber-500"
                  />
                  <div>
                    <span className="text-gray-700 dark:text-gray-300 font-medium">Veranstaltungsstatistik</span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Übersicht aller Veranstaltungen mit Teilnehmerzahlen</p>
                  </div>
                </label>

                <label className="flex items-start gap-3 cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-800/30 p-2 rounded-lg transition-colors">
                  <input
                    type="checkbox"
                    checked={selectedFinanzamtChapters.ruecklagennachweis}
                    onChange={(e) => setSelectedFinanzamtChapters(prev => ({ ...prev, ruecklagennachweis: e.target.checked }))}
                    className="w-5 h-5 mt-0.5 text-amber-600 border-gray-300 dark:border-gray-600 rounded focus:ring-amber-500"
                  />
                  <div>
                    <span className="text-gray-700 dark:text-gray-300 font-medium">Rücklagennachweis</span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Bildung und Entwicklung der Rücklagen nach § 62 AO</p>
                  </div>
                </label>
              </div>

              <button
                onClick={handleFinanzamtExport}
                disabled={finanzamtExportLoading || !Object.values(selectedFinanzamtChapters).some(v => v)}
                className="mt-auto w-full flex items-center justify-center gap-2 px-6 py-3 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-400 text-white font-medium rounded-lg transition-colors"
              >
                {finanzamtExportLoading ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Wird erstellt...
                  </>
                ) : (
                  <>
                    <BuildingOffice2Icon className="h-5 w-5" />
                    Finanzamt-Unterlagen {selectedExportYear}
                  </>
                )}
              </button>
            </div>
          </div>
        </section>

        {/* Präsentation zur Mitgliederversammlung */}
        <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-primary-600 dark:text-primary-400 flex items-center gap-2.5 mb-6">
            <PresentationChartBarIcon className="h-5 w-5" /> Mitgliederversammlung
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-2">
            Foliensatz im Format 16:9 für den Beamer — mit Tagesordnung, Kennzahlen, Kassenbericht
            und Abstimmungsfolien.
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 flex items-start gap-2">
            <InformationCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>
              Die Folien enthalten bewusst keine Mitgliederliste und keine namentlichen
              Beitragsrückstände — offene Beiträge erscheinen nur als Summe.
            </span>
          </p>

          {/* Eigene Jahresauswahl: berichtet wird über ein abgeschlossenes Jahr,
              oben im Jahresordner steht dagegen meist das laufende. */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600 mb-4">
            <AttributeLabel iconKey="year" className="flex mb-2" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
              Geschäftsjahr, über das berichtet wird
            </AttributeLabel>
            <select
              value={selectedVersammlungYear}
              onChange={(e) => setSelectedVersammlungYear(Number(e.target.value))}
              className="w-full sm:w-48 px-3 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            {selectedVersammlungYear >= new Date().getFullYear() && (
              <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
                {selectedVersammlungYear} ist noch nicht abgeschlossen — die Zahlen sind ein
                Zwischenstand.
              </p>
            )}
          </div>

          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-5 border border-gray-200 dark:border-gray-600">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Wählen Sie die Tagesordnungspunkte, die im Foliensatz erscheinen sollen:
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 mb-5">
              {VERSAMMLUNG_KAPITEL.map(({ key, label, hinweis }) => (
                <label
                  key={key}
                  className="flex items-start gap-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 p-2 rounded-lg transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={Boolean(selectedVersammlungChapters[key])}
                    onChange={(e) => setSelectedVersammlungChapters(prev => ({ ...prev, [key]: e.target.checked }))}
                    className="w-5 h-5 mt-0.5 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                  />
                  <div className="min-w-0">
                    <span className="text-gray-700 dark:text-gray-300 font-medium">{label}</span>
                    {hinweis && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">{hinweis}</p>
                    )}
                  </div>
                </label>
              ))}
            </div>

            <button
              onClick={handleVersammlungExport}
              disabled={versammlungLoading}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white font-medium rounded-lg transition-colors"
            >
              {versammlungLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Folien werden erstellt...
                </>
              ) : (
                <>
                  <PresentationChartBarIcon className="h-5 w-5" />
                  Präsentation {selectedVersammlungYear} erstellen
                </>
              )}
            </button>
          </div>
        </section>
      </div>

      {/* Documents Download Progress */}
      <DownloadProgressModal
        visible={documentsDownload.progress.active || (!!documentsDownload.progress.error && !documentsDownload.progress.active)}
        progress={documentsDownload.progress}
        onCancel={documentsDownload.cancelDownload}
        onClose={() => {
          const wasSuccessful = !documentsDownload.progress.active && !documentsDownload.progress.error;
          documentsDownload.resetProgress();
          if (wasSuccessful) {
            setConfirmModal({
              visible: true,
              type: 'success',
              title: 'Erfolgreich',
              message: 'Alle Dokumente wurden heruntergeladen!',
              variant: 'single',
              button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
            });
          }
        }}
      />

      {/* Dogs Download Progress */}
      <DownloadProgressModal
        visible={dogsDownload.progress.active || (!!dogsDownload.progress.error && !dogsDownload.progress.active)}
        progress={dogsDownload.progress}
        onCancel={dogsDownload.cancelDownload}
        onClose={() => {
          const wasSuccessful = !dogsDownload.progress.active && !dogsDownload.progress.error;
          dogsDownload.resetProgress();
          if (wasSuccessful) {
            setConfirmModal({
              visible: true,
              type: 'success',
              title: 'Erfolgreich',
              message: 'Alle Hunde-Dokumente wurden heruntergeladen!',
              variant: 'single',
              button: { text: 'OK', onPress: () => setConfirmModal(prev => ({ ...prev, visible: false })) }
            });
          }
        }}
      />

      <ConfirmationModal
        visible={confirmModal.visible}
        onClose={confirmModal.onClose}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        buttons={confirmModal.buttons}
        button={confirmModal.button}
      />

      {/* Category Management Modal */}
      <CategoryManagementModal
        isOpen={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
      />
    </div>
  );
};

export default AdminFinanceExport;
